import { useRedirectForProductShare } from '@/components/Checkout/_helpers/hooks/useRedirectForProductShare'
import { FixedReportIssueButton } from '@/components/FixedReportIssueButton'
import Footer from '@/components/Footer'
import T from '@/components/Helpers/Translate'
import { ProductsGrid } from '@/components/Products/ProductsGrid'
import Header from '@/components/_headers/Header'
import { NavigationMenuContext } from '@/components/_headers/_shared/navigationMenuContext'
import { Skeleton } from '@/components/_ui/Skeleton'
import type { CosmicProductType } from '@/lib/cms/cosmic/types/CosmicProductType'
import type { CMSError } from '@/lib/cms/types/CMSError'
import { useSearchProducts } from '@/lib/hooks/helpers/useSearchProducts'
import useGlobalStore from '@/store/useGlobalStore'
import { AspectRatio } from '@radix-ui/react-aspect-ratio'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

type Props = {
  children: ReactNode
  products?: CosmicProductType[] | { error: CMSError } | null
}

const LandingPageLayout = ({ children, products }: Props) => {
  const {
    app: { isSearchBarActive },
  } = useGlobalStore()

  useRedirectForProductShare({ products })

  const { isLoading, filteredProducts } = useSearchProducts({ products })

  const [isNavigationMenuOpen, setIsNavigationMenuOpen] = useState(false)
  const [navigationHeight, setNavigationHeight] = useState(0)
  const [navigationMenuValue, setNavigationMenuValue] = useState('')
  const [offsetHeight, setOffsetHeight] = useState(0)
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false)
  const [currentSearchView, setCurrentSearchView] = useState<ReactNode | null>(
    null,
  )
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Update the view based on the search results, doing it like this rather than just
    // if/else returns allows us to have a nice transition between the different views

    const updateView = (view: ReactNode) => {
      setIsVisible(false)
      setTimeout(() => {
        setCurrentSearchView(view)
        setIsVisible(true)
      }, 300)
    }

    if (isLoading) {
      updateView(
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 8 }, (_, index) => (
            <AspectRatio key={index} ratio={1}>
              <Skeleton className="size-full" />
            </AspectRatio>
          ))}
        </div>,
      )
    } else if (!filteredProducts) {
      updateView(
        <div>
          <T>start_type_to_search_products</T>
        </div>,
      )
    } else if (filteredProducts.length === 0) {
      updateView(
        <div>
          <T>no_products_found</T>
        </div>,
      )
    } else {
      updateView(<ProductsGrid products={filteredProducts} />)
    }
  }, [isLoading, filteredProducts])

  return (
    <>
      <FixedReportIssueButton />
      <NavigationMenuContext.Provider
        value={{
          navigationMenuValue,
          setNavigationMenuValue,
          isNavigationMenuOpen,
          setIsNavigationMenuOpen,
          navigationHeight,
          setNavigationHeight,
          offsetHeight,
          setOffsetHeight,
          isAccountMenuOpen,
          setIsAccountMenuOpen,
        }}
      >
        <div
          style={{ zIndex: 900 }}
          className={
            isNavigationMenuOpen
              ? 'pointer-events-none fixed top-0 left-0 h-screen w-screen bg-black/60 transition-all duration-300 ease-in-out'
              : 'pointer-events-none fixed top-0 left-0 h-screen w-screen opacity-0 transition duration-500 ease-in-out'
          }
        />
        <div style={{ position: 'relative', zIndex: 1000 }}>
          <Header />
        </div>

        {isSearchBarActive ? (
          <div
            className={`m-auto min-h-screen max-w-[1200px] border-t p-5 transition-all duration-300${
              isVisible ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {currentSearchView}
          </div>
        ) : (
          children
        )}

        <Footer />
      </NavigationMenuContext.Provider>
    </>
  )
}

export default LandingPageLayout
